import client from "./client";

export type PrivacyRequestType = "export" | "deletion";
export type PrivacyRequestStatus =
  | "pending"
  | "processing"
  | "completed"
  | "denied"
  | "cancelled";

export interface PrivacyRequest {
  id: string;
  user_id: string;
  employee_id: string | null;
  request_type: PrivacyRequestType;
  status: PrivacyRequestStatus;
  reason: string | null;
  admin_notes: string | null;
  processed_by: string | null;
  processed_at: string | null;
  scheduled_for: string | null;
  expires_at: string | null;
  created_at: string;
  has_download: boolean;
}

export type AdminAction = "approve" | "deny" | "cancel";

export async function requestMyExport(reason?: string): Promise<PrivacyRequest> {
  const res = await client.post<PrivacyRequest>("/privacy/export-my-data", {
    reason,
  });
  return res.data;
}

export async function requestMyDeletion(reason?: string): Promise<PrivacyRequest> {
  const res = await client.post<PrivacyRequest>("/privacy/delete-my-data", {
    reason,
  });
  return res.data;
}

export async function listMyPrivacyRequests(): Promise<PrivacyRequest[]> {
  const res = await client.get<PrivacyRequest[]>("/privacy/my-requests");
  return res.data;
}

export async function listPrivacyRequests(
  status?: PrivacyRequestStatus
): Promise<PrivacyRequest[]> {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  const res = await client.get<PrivacyRequest[]>("/privacy/requests", { params });
  return res.data;
}

export async function actOnPrivacyRequest(
  requestId: string,
  action: AdminAction,
  notes?: string
): Promise<PrivacyRequest> {
  const res = await client.put<PrivacyRequest>(
    `/privacy/requests/${requestId}`,
    { action, notes }
  );
  return res.data;
}

/**
 * Fetches the completed export payload and triggers a browser download as
 * a pretty-printed JSON file. The backend returns the raw object — we
 * turn it into a Blob client-side so we don't have to stream bytes back.
 */
export async function downloadPrivacyExport(
  requestId: string,
  filename = "my-data.json"
): Promise<void> {
  const res = await client.get<Record<string, unknown>>(
    `/privacy/requests/${requestId}/download`
  );
  const blob = new Blob([JSON.stringify(res.data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
